import { Image } from 'expo-image';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Modal, StyleSheet, View } from 'react-native';

import { claimIcon } from '@/assets/images/icons';
import { VerifyCodeData, verifyCodeResolver } from '@/lib/validations/VerifyCodeSchema';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import GradientCircularButton from '../buttons/GradientCircularButton';
import CodeInputField from '../inputs/CodeInputField';
import Text from '../Text';
import ModalCard from './ModalCard';

interface ClaimNpoVerifyCodeModalProps {
  visible: boolean;
  onValidatePressed: (code: string) => void;
  onCloseModal: () => void;
}

const ClaimNpoVerifyCodeModal = ({ visible, onValidatePressed, onCloseModal }: ClaimNpoVerifyCodeModalProps) => {
  const { control, handleSubmit, watch, reset } = useForm<VerifyCodeData>({
    mode: 'onChange',
    defaultValues: { verificationCode: '' },
    resolver: verifyCodeResolver
  });

  const verificationCodeWatched = watch('verificationCode');

  useEffect(() => {
    if (verificationCodeWatched && verificationCodeWatched.length === 6) {
      handleSubmit(onValidate)();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationCodeWatched]);

  const onValidate: SubmitHandler<VerifyCodeData> = async (data) => {
    onValidatePressed(data.verificationCode);
    reset();
  };

  return (
    <Modal animationType="fade" transparent={true} visible={visible} statusBarTranslucent onRequestClose={onCloseModal}>
      <ModalCard closeModal={onCloseModal} showCloseButton closeOnTapOutside>
        <View style={styles.cardContainer}>
          <GradientCircularButton disabled size={92}>
            <Image style={styles.icon} source={claimIcon} contentFit={'contain'} />
          </GradientCircularButton>
          <Text style={styles.descriptionText}>{i18n.t('verifyCode.description')}</Text>

          <View style={styles.input}>
            <CodeInputField control={control} name="verificationCode" keyboardType="default" />
          </View>
          <BrandedButton
            textProps={{ ellipsizeMode: 'middle', numberOfLines: 2 }}
            textStyles={styles.buttonTextStyle}
            style={styles.button}
            backGroundColor="#FE9E22"
            onPress={handleSubmit(onValidate)}>
            {i18n.t('mix.submit')}
          </BrandedButton>
        </View>
      </ModalCard>
    </Modal>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    width: '100%',
    backgroundColor: 'white',
    flexDirection: 'column',
    borderRadius: 20,
    paddingHorizontal: 8,
    paddingBottom: 16,
    paddingTop: 20,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 15
  },

  descriptionText: {
    fontSize: 16,
    marginBottom: 12,
    textAlign: 'center'
  },

  icon: {
    width: 48,
    height: 48
  },

  input: {
    width: '100%'
  },

  button: {
    marginTop: 15,
    width: '100%'
  },

  buttonTextStyle: {
    alignSelf: 'center',
    textAlign: 'center',
    textAlignVertical: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10
  }
});

export default ClaimNpoVerifyCodeModal;
